import { useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/presentation/components/ui/card'
// import { useNavigate } from 'react-router-dom'
import DriversFilter from '@/presentation/components/dashboard/Filter'
import ViewToggle from '@/presentation/components/dashboard/ViewToggle'
import DriversTable from '@/presentation/components/dashboard/DriversTable'
import DriversCards from '@/presentation/components/dashboard/DriversCards'
import { Driver } from '@/core/domain/entities/Driver'
import { useDashboardStore } from '@/core/stores/dashboardStore'

type DriverStatistics = {
  driverId: string
  totalRides: number
  completedRides: number
  comissionEarnings: number
}

type DashboardDriverTabProps = {
  drivers: Driver[]
  summary?: {
    totalDrivers: number
    onlineDrivers: number
    offlineDrivers: number
    averageRating: number
    onlinePercentage: number
    ridesPerDriverList: DriverStatistics[]
  } | null
  isLoadingStats?: boolean
}

export default function DashboardDriverTab({
  drivers,
  summary,
  isLoadingStats = false,
}: DashboardDriverTabProps) {
  // const navigate = useNavigate()

  const { viewMode, setViewMode } = useDashboardStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [assignmentFilter, setAssignmentFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all')
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'Online' | 'Offline'
  >('all')
  const [totalRidesFilter, setTotalRidesFilter] = useState<
    'all' | '0' | '1-5' | '6-10' | '11-25' | '26-50' | '51-100' | '100+'
  >('all')
  const [acceptsCashFilter, setAcceptsCashFilter] = useState<
    'all' | '0' | '1' | '2'
  >('all')
  const [sortByField, setSortByField] = useState<string>('status')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const driverStatisticsList = useMemo(
    () => summary?.ridesPerDriverList ?? [],
    [summary]
  )

  const driverStatisticsMap = useMemo(() => {
    const statisticsMap = new Map<string, DriverStatistics>()
    driverStatisticsList.forEach((stats) => {
      statisticsMap.set(stats.driverId, stats)
    })
    return statisticsMap
  }, [driverStatisticsList])

  const filteredDrivers = useMemo(() => {
    const availableDrivers = drivers ?? []

    const matchesSearchTerm = (currentDriver: Driver) => {
      const driverName = (currentDriver.fullName ?? '').toLowerCase()
      return driverName.includes(searchTerm.toLowerCase())
    }

    const matchesAssignmentFilter = (currentDriver: Driver) => {
      return (
        assignmentFilter === 'all' ||
        (currentDriver.hasSubscription ? 'active' : 'inactive') ===
          assignmentFilter
      )
    }

    const matchesStatusFilter = (currentDriver: Driver) => {
      return (
        statusFilter === 'all' ||
        (statusFilter === 'Online'
          ? !!currentDriver.isOnline
          : !currentDriver.isOnline)
      )
    }

    const matchesTotalRidesFilter = (currentDriver: Driver) => {
      if (totalRidesFilter === 'all') return true

      const driverStatistics = driverStatisticsMap.get(currentDriver.id)
      const totalRides = driverStatistics?.totalRides ?? 0

      switch (totalRidesFilter) {
        case '0':
          return totalRides === 0
        case '1-5':
          return totalRides >= 1 && totalRides <= 5
        case '6-10':
          return totalRides >= 6 && totalRides <= 10
        case '11-25':
          return totalRides >= 11 && totalRides <= 25
        case '26-50':
          return totalRides >= 26 && totalRides <= 50
        case '51-100':
          return totalRides >= 51 && totalRides <= 100
        case '100+':
          return totalRides > 100
        default:
          return true
      }
    }

    const matchesAcceptsCashFilter = (currentDriver: Driver) => {
      if (acceptsCashFilter === 'all') return true
      return String(currentDriver.acceptsCash ?? '0') === acceptsCashFilter
    }

    const getDriverStatistics = (currentDriver: Driver) => {
      return driverStatisticsMap.get(currentDriver.id)
    }

    const getSortValue = (currentDriver: Driver, sortField: string) => {
      switch (sortField) {
        case 'name':
          return (currentDriver.fullName ?? '').toLowerCase()
        case 'status':
          return currentDriver.isOnline ? 1 : 0
        case 'totalRides': {
          const statistics = getDriverStatistics(currentDriver)
          return statistics?.totalRides ?? 0
        }
        case 'commission': {
          const statistics = getDriverStatistics(currentDriver)
          return statistics?.comissionEarnings ?? 0
        }
        case 'subscription':
          return currentDriver.hasSubscription ? 1 : 0
        case 'rating':
          return currentDriver.rating ?? 0
        case 'lastRide':
          return currentDriver.lastRideAt ?? ''
        default:
          return 0
      }
    }

    const compareDrivers = (driverA: Driver, driverB: Driver) => {
      if (sortByField !== 'status') {
        const valueA = getSortValue(driverA, sortByField)
        const valueB = getSortValue(driverB, sortByField)

        let comparison = 0
        if (valueA < valueB) comparison = -1
        else if (valueA > valueB) comparison = 1

        return sortOrder === 'asc' ? comparison : -comparison
      }

      const statsA = getDriverStatistics(driverA)
      const statsB = getDriverStatistics(driverB)

      const statusA = driverA.isOnline ? 1 : 0
      const statusB = driverB.isOnline ? 1 : 0
      if (statusA !== statusB) {
        return statusB - statusA
      }

      const ridesA = statsA?.totalRides ?? 0
      const ridesB = statsB?.totalRides ?? 0
      if (ridesA !== ridesB) {
        return ridesB - ridesA
      }

      const commissionA = statsA?.comissionEarnings ?? 0
      const commissionB = statsB?.comissionEarnings ?? 0
      return commissionB - commissionA
    }

    return availableDrivers
      .filter(
        (currentDriver) =>
          matchesSearchTerm(currentDriver) &&
          matchesAssignmentFilter(currentDriver) &&
          matchesStatusFilter(currentDriver) &&
          matchesTotalRidesFilter(currentDriver) &&
          matchesAcceptsCashFilter(currentDriver)
      )
      .sort(compareDrivers)
  }, [
    drivers,
    searchTerm,
    assignmentFilter,
    statusFilter,
    totalRidesFilter,
    acceptsCashFilter,
    driverStatisticsMap,
    sortByField,
    sortOrder,
  ])

  const handleSort = (field: string) => {
    if (sortByField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByField(field)
      setSortOrder('asc')
    }
  }

  // const handleDriverClick = (selectedDriverId: string) => {
  //   navigate(`/driver/${selectedDriverId}`)
  // }

  return (
    <div className="space-y-6">
      <DriversFilter
        searchTerm={searchTerm}
        assignment={assignmentFilter}
        status={statusFilter}
        totalRides={totalRidesFilter}
        acceptsCash={acceptsCashFilter}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        onAssignmentChange={(value: string) =>
          setAssignmentFilter(value as 'all' | 'active' | 'inactive')
        }
        onStatusChange={(value: string) =>
          setStatusFilter(value as 'all' | 'Online' | 'Offline')
        }
        onTotalRidesChange={(value: string) =>
          setTotalRidesFilter(
            value as
              | 'all'
              | '0'
              | '1-5'
              | '6-10'
              | '11-25'
              | '26-50'
              | '51-100'
              | '100+'
          )
        }
        onAcceptsCashChange={(value: string) =>
          setAcceptsCashFilter(value as 'all' | '0' | '1' | '2')
        }
      />

      <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />

      <Card>
        <CardHeader>
          <CardTitle>Seus Motoristas</CardTitle>
          <CardDescription>
            Acompanhe o desempenho e produtividade dos motoristas sob sua gestão
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div
            className={`sm:hidden ${viewMode === 'cards' ? 'block' : 'hidden'}`}
          >
            <DriversCards
              filteredDrivers={filteredDrivers}
              driverStatisticsMap={driverStatisticsMap}
              isLoadingStats={isLoadingStats}
            />
          </div>

          <div
            className={`overflow-x-auto ${viewMode === 'cards' ? 'hidden sm:block' : ''}`}
          >
            <DriversTable
              filteredDrivers={filteredDrivers}
              driverStatisticsMap={driverStatisticsMap}
              sortByField={sortByField}
              sortOrder={sortOrder}
              handleSort={handleSort}
              isLoadingStats={isLoadingStats}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
